import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Table,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Spinner,
} from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiPlus } from "@mdi/js";
import { mdiMagnify, mdiInformationOutline } from "@mdi/js";
import defaultAvatar from "../../images/avatar.svg";
import PageLayout from "../../components/common/PageLayout";
import CustomPagination from "../../components/common/Pagination";
import { PartialLoadingSpinner } from "../../components/LoadingSpinner";
import TruncatedTextWithTooltip from "../../components/common/TruncatedTextWithTooltip";
import {
  fetchAgents,
  searchAgents,
  setCurrentAgentPage,
  clearRefetchFlag,
  setSearchTerm,
  clearSearchResults,
} from "./slice";
import { usePartialLoading } from "../../hooks/usePartialLoading";
import "./agentManagement.css";

const AgentManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const {
    agents,
    agentsCount,
    getAgentsStatus,
    currentAgentPage,
    shouldRefetchAgents,
    searchTerm,
    searchAgentsStatus,
  } = useSelector((state) => state.agents);

  // Local state
  const hasInitialized = useRef(false);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchValueRef = useRef(""); // Store search value in ref instead of state

  // Partial loading hook
  const agentsLoading = usePartialLoading("agents-content");

  const agentsPerPage = 10;

  const handleNewAgent = () => {
    navigate("/agent-management/new");
  };

  const handleEditAgent = (agentId) => {
    navigate(`/agent-management/${agentId}/edit`);
  };

  // Search handler - NO immediate Redux updates to prevent re-render
  const handleSearchInputChange = (e) => {
    const value = e.target.value;

    // Mark that user is typing to enable focus restoration
    wasTypingRef.current = true;

    // Store value in ref for later use
    searchValueRef.current = value;

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search API call
    searchTimeoutRef.current = setTimeout(() => {
      // Only NOW update Redux with the search term
      dispatch(setSearchTerm(value));

      if (value.trim()) {
        agentsLoading.startLoading();
        dispatch(searchAgents({ searchTerm: value.trim() }));
      } else {
        agentsLoading.startLoading();
        dispatch(clearSearchResults());
        dispatch(setCurrentAgentPage(1));
        dispatch(fetchAgents(1));
      }
    }, 400);
  };

  const handlePageChange = (page) => {
    agentsLoading.startLoading();
    dispatch(setCurrentAgentPage(page));
    dispatch(fetchAgents(page));
  };

  // Scroll to top on component mount
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, 0);
  }, []);

  // Fetch agents on component mount
  useEffect(() => {
    if (
      getAgentsStatus !== "loading" &&
      agents.length === 0 &&
      !hasInitialized.current
    ) {
      // Don't use agentsLoading for initial load - we handle it inline
      dispatch(fetchAgents(currentAgentPage));
    }
  }, [dispatch, currentAgentPage, agents, getAgentsStatus]);

  // Set hasInitialized only when agents are successfully loaded
  useEffect(() => {
    if (getAgentsStatus === "success" && !hasInitialized.current) {
      hasInitialized.current = true;
    }
  }, [getAgentsStatus]);

  // Refetch agents when needed (after create/update)
  useEffect(() => {
    if (shouldRefetchAgents) {
      agentsLoading.startLoading();
      dispatch(fetchAgents(currentAgentPage));
      dispatch(clearRefetchFlag());
    }
  }, [shouldRefetchAgents, dispatch, currentAgentPage]);

  // Stop loading when operations complete
  useEffect(() => {
    if (getAgentsStatus === "success" || getAgentsStatus === "failed") {
      agentsLoading.stopLoading();
    }
  }, [getAgentsStatus]);

  useEffect(() => {
    if (searchAgentsStatus === "success" || searchAgentsStatus === "failed") {
      agentsLoading.stopLoading();
    }
  }, [searchAgentsStatus]);

  // Initialize input value from Redux state on mount
  useEffect(() => {
    if (searchInputRef.current && searchTerm) {
      searchInputRef.current.value = searchTerm;
      searchValueRef.current = searchTerm;
    }
  }, []); // Only on mount

  // Track if user was typing to restore focus
  const wasTypingRef = useRef(false);

  // Preserve focus after search API response by restoring input value
  useEffect(() => {
    if (
      searchInputRef.current &&
      (searchAgentsStatus === "success" || searchAgentsStatus === "failed")
    ) {
      // Use setTimeout to ensure this runs after all other re-renders
      setTimeout(() => {
        if (searchInputRef.current && wasTypingRef.current) {
          const currentValue = searchValueRef.current;
          const cursorPosition = currentValue.length; // Place cursor at end

          // Restore input value if needed
          if (searchInputRef.current.value !== currentValue) {
            searchInputRef.current.value = currentValue;
          }

          // Always restore focus and cursor position after search
          searchInputRef.current.focus();
          searchInputRef.current.setSelectionRange(
            cursorPosition,
            cursorPosition
          );

          console.log("Focus restored after search response"); // Debug log
        }
      }, 10);
    }
  }, [searchAgentsStatus]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Listen for agent updates from other tabs
  useEffect(() => {
    let channel;

    try {
      channel = new BroadcastChannel('agent_updates');

      channel.onmessage = (event) => {
        const { type } = event.data;
        if (type === 'AGENT_CREATED' || type === 'AGENT_UPDATED' || type === 'AGENT_DELETED') {
          // Refresh agent list when any agent change happens in another tab
          agentsLoading.startLoading();
          dispatch(fetchAgents(currentAgentPage));
        }
      };
    } catch (error) {
      console.error('Failed to setup BroadcastChannel:', error);
    }

    // Backup mechanism: refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const lastUpdate = localStorage.getItem('agent_last_update');
        if (lastUpdate) {
          const timeDiff = Date.now() - parseInt(lastUpdate, 10);
          // Refresh if update happened in last 5 minutes
          if (timeDiff < 5 * 60 * 1000) {
            agentsLoading.startLoading();
            dispatch(fetchAgents(currentAgentPage));
            localStorage.removeItem('agent_last_update');
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (channel) {
        channel.close();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch, currentAgentPage, agentsLoading]);

  const EmptyState = () => (
    <div className="empty-state">
      <div className="empty-state-content">
        <p className="empty-message">No Agents yet</p>
        <Button
          variant="primary"
          onClick={handleNewAgent}
          className="empty-state-btn"
        >
          <Icon path={mdiPlus} size={0.8} className="me-2" />
          New Agent
        </Button>
      </div>
    </div>
  );

  const AgentTable = () => (
    <>
      <div className="search-section mb-4">
        <InputGroup className="search-input">
          <InputGroup.Text>
            <Icon path={mdiMagnify} size={0.8} />
          </InputGroup.Text>
          <Form.Control
            ref={searchInputRef}
            placeholder="Search Agent Name"
            defaultValue={searchTerm || ""}
            onChange={handleSearchInputChange}
          />
        </InputGroup>
      </div>

      <div
        id="agents-content"
        className="table-container"
        style={{ position: "relative", minHeight: "300px" }}
      >
        {/* Desktop Table View */}
        <Table responsive className="agents-table d-none d-xl-table">
          <thead>
            <tr>
              <th style={{ width: "237px" }}>Name</th>
              <th style={{ width: "132px" }}>Phone</th>
              <th>Email</th>
              <th style={{ minWidth: "256px" }}>Agency</th>
              <th style={{ width: "180px" }}>Note</th>
              <th style={{ width: "80px" }}></th>
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => (
              <tr
                key={agent.id}
                onClick={() => handleEditAgent(agent.id)}
                className="agent-row"
              >
                <td style={{ width: "237px" }}>
                  <div className="agent-name">
                    <div className="agent-avatar">
                      <img
                        src={
                          agent.profile_image?.presigned_url || defaultAvatar
                        }
                        alt={agent.name}
                        className="agent-avatar-image"
                      />
                    </div>
                    {agent.name}
                  </div>
                </td>
                <td style={{ width: "132px" }}>{agent.phone}</td>
                <td>{agent.email}</td>
                <td style={{ minWidth: "256px" }}>
                  <div className="agency-info">
                    <div className="agency-name">{agent.agency}</div>
                    <div className="agency-address">
                      {agent.address?.formatted_address}
                    </div>
                  </div>
                </td>
                <td style={{ width: "180px" }}>
                  <TruncatedTextWithTooltip
                    text={agent.note || ""}
                    maxWidth="164px"
                    className="agent-note"
                    placement="top"
                    id={`agent-note-${agent.id}`}
                  />
                </td>
                <td style={{ width: "80px" }}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEditAgent(agent.id)}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* Mobile/Tablet Card View */}
        <div className="agents-mobile-cards d-xl-none">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="agent-card"
              onClick={() => handleEditAgent(agent.id)}
            >
              <div className="agent-card-content">
                <div className="agent-card-header">
                  <div className="agent-card-info">
                    <div className="agent-avatar">
                      <img
                        src={
                          agent.profile_image?.presigned_url || defaultAvatar
                        }
                        alt={agent.name}
                        className="agent-avatar-image"
                      />
                    </div>
                    <div className="agent-card-name-section">
                      <div className="agent-card-name">
                        {agent.name}
                        {agent.note && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`agent-note-mobile-${agent.id}`}>
                                {agent.note}
                              </Tooltip>
                            }
                          >
                            <Icon
                              path={mdiInformationOutline}
                              size={0.7}
                              className="ms-2 text-muted agent-note-icon"
                            />
                          </OverlayTrigger>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditAgent(agent.id);
                    }}
                  >
                    Edit
                  </Button>
                </div>
                <div className="agent-card-body">
                  <div className="agent-card-first-row">
                    {agent.phone && (
                      <div className="agent-card-contact">{agent.phone}</div>
                    )}
                    {agent.email && (
                      <div className="agent-card-contact">{agent.email}</div>
                    )}
                    {agent.agency && (
                      <div className="agent-card-agency">
                        <div className="agency-name">{agent.agency}</div>
                      </div>
                    )}
                  </div>
                  {agent.address?.formatted_address && (
                    <div className="agency-address">
                      {agent.address.formatted_address}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {agentsCount > agentsPerPage && (
        <div className="pagination-wrapper">
          <CustomPagination
            totalItems={agentsCount}
            itemsPerPage={agentsPerPage}
            currentPage={currentAgentPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );

  // Check if this is the initial page load (no data yet and loading)
  const isInitialLoading =
    !hasInitialized.current &&
    (getAgentsStatus === "loading" || agentsLoading.isLoading);

  const headerActions = isInitialLoading ? (
    <Button variant="primary" disabled>
      <Spinner
        as="span"
        animation="border"
        size="sm"
        role="status"
        aria-hidden="true"
      />
    </Button>
  ) : (
    <Button variant="primary" onClick={handleNewAgent}>
      <Icon path={mdiPlus} size={0.8} className="me-1" />
      New Agent
    </Button>
  );

  return (
    <PageLayout
      title="Agent Management"
      headerActions={headerActions}
      className="agent-management-page"
    >
      {isInitialLoading ? (
        <div style={{ position: "relative", minHeight: "calc(100vh - 200px)" }}>
          <PartialLoadingSpinner />
        </div>
      ) : agents.length === 0 &&
        (!searchTerm || searchTerm.trim() === "") &&
        !agentsLoading.isLoading &&
        getAgentsStatus === "success" ? (
        <EmptyState />
      ) : (
        <AgentTable />
      )}
    </PageLayout>
  );
};

export default AgentManagement;
